import { buildApiUrl } from '../config/api.js';

class CalendarService {
  constructor() {
    this.authenticatedFetch = null;
  }

  // Método para establecer la función de fetch autenticada
  setAuthenticatedFetch(fetchFn) {
    this.authenticatedFetch = fetchFn;
  }

  /**
   * Obtiene los datos del calendario para un rango de fechas específico
   * @param {Date} startDate - Fecha de inicio del rango
   * @param {Date} endDate - Fecha de fin del rango
   * @returns {Promise<Object>} Datos del calendario con check-ins, check-outs y mantenimientos
   */
  async getCalendarData(startDate, endDate) {
    try {
      const startDateStr = startDate.toISOString().split('T')[0];
      const endDateStr = endDate.toISOString().split('T')[0];
      
      const url = buildApiUrl(`/api/admin/calendar?startDate=${startDateStr}&endDate=${endDateStr}`);
      
      const response = this.authenticatedFetch 
        ? await this.authenticatedFetch(url, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json'
            }
          })
        : await fetch(url, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem('authToken')}`
            }
          });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(`Error ${response.status}: ${errorData.message || response.statusText}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error al obtener datos del calendario:', error);
      throw error;
    }
  }

  /**
   * Obtiene la disponibilidad de una bicicleta por día (mantenimientos y reservas con id)
   * @param {number|string} vehicleId - ID del vehículo
   * @param {Date} startDate - Inicio del rango
   * @param {Date} endDate - Fin del rango
   * @returns {Promise<Object>} { success, data: { "YYYY-MM-DD": [{ type, maintenanceId?|bookingId? }] } }
   */
  async getVehicleAvailability(vehicleId, startDate, endDate) {
    try {
      const startDateStr = startDate.toISOString().split('T')[0];
      const endDateStr = endDate.toISOString().split('T')[0];
      const url = buildApiUrl(`/api/admin/calendar/vehicle/${vehicleId}?startDate=${startDateStr}&endDate=${endDateStr}`);

      const response = this.authenticatedFetch
        ? await this.authenticatedFetch(url, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' }
          })
        : await fetch(url, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem('authToken')}`
            }
          });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(`Error ${response.status}: ${errorData.message || response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error al obtener disponibilidad del vehículo:', error);
      throw error;
    }
  };

  /**
   * Obtiene estadísticas del mes actual
   * @param {Date} date - Fecha del mes a consultar
   * @returns {Promise<Object>} Estadísticas del mes
   */
  async getMonthStats(date) {
    try {
      const year = date.getFullYear();
      const month = date.getMonth() + 1;
      
      const url = buildApiUrl(`/api/admin/calendar/stats?year=${year}&month=${month}`);
      
      const response = this.authenticatedFetch 
        ? await this.authenticatedFetch(url, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json'
            }
          })
        : await fetch(url, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem('authToken')}`
            }
          });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(`Error ${response.status}: ${errorData.message || response.statusText}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error al obtener estadísticas del mes:', error);
      throw error;
    }
  }
}

export default new CalendarService();
